"use client";

import React from "react";
import { ArrowRight, PhoneCall, ShieldCheck, Sparkles, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: 1,
  title: "Diagnóstico técnico gratuito",
  description: "Avaliação detalhada dos seus bicos e circuitos de pulverização."
}, {
  id: 2,
  title: "Representante oficial em Portugal",
  description: "Acesso direto à tecnologia e catálogo da Spraying Systems Co."
}, {
  id: 3,
  title: "Eficiência e poupança",
  description: "Redução do consumo de água, químicos e paragens de manutenção."
}];
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    whileInView: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.3
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="calltoaction" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...animationProps}>
          <div className="relative overflow-hidden rounded-3xl bg-primary px-6 py-12 text-primary-foreground shadow-2xl sm:px-12 md:py-16 lg:px-16">
            {/* Background subtle geometric accents */}
            <div className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-secondary/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-secondary/10 blur-3xl" />

            <div className="relative z-10 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 px-3.5 py-1 text-xs font-medium text-primary-foreground backdrop-blur-xs">
                  <Sparkles className="size-3.5" />
                  <span>Eurocontrol & Spraying Systems Co.</span>
                </div>

                <h2 className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl lg:text-5xl font-default">
                  Otimize os seus processos de pulverização industrial
                </h2>

                <p className="mt-4 max-w-2xl text-base text-primary-foreground/90 sm:text-lg">
                  Fale com a nossa equipa de engenharia para avaliar as suas necessidades técnicas, reduzir desperdícios ou agendar uma demonstração prática.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button asChild size="lg" className="bg-secondary text-secondary-foreground shadow-md transition-[color,background-color,box-shadow] duration-200 hover:bg-secondary/90">
                    <Link to="/ContactsPage" className="inline-flex items-center gap-2">
                      <span>Solicite uma demonstração</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="border-primary-foreground/25 bg-primary-foreground/10 text-primary-foreground transition-[color,background-color,border-color] duration-200 hover:bg-primary-foreground/20 hover:text-primary-foreground">
                    <Link to="/ContactsPage" className="inline-flex items-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Contacte-nos</span>
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-1">
                  {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="rounded-2xl border border-primary-foreground/15 bg-primary-foreground/5 p-4.5 backdrop-blur-xs transition-[background-color,border-color] duration-200 hover:bg-primary-foreground/10">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary/30 text-primary-foreground">
                          {i === 0 && <Wrench className="size-4" />}
                          {i === 1 && <ShieldCheck className="size-4" />}
                          {i === 2 && <Sparkles className="size-4" />}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-primary-foreground font-default">
                            {benefit.title}
                          </h3>
                          <p className="mt-1 text-xs text-primary-foreground/80 leading-relaxed">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}